import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router';
import type { Page } from '@/api/types';
import { useFormFactor } from '@/lib/formFactor';
import { Tag, Dot } from '@/components/Tag';
import { Icon } from '@/components/Icon';
import { Alert, ErrorState, Skeleton } from '@/components/States';
import { date, dateTime, money, num, qty } from '@/lib/format';
import { useSession } from '@/stores/session';
import { ConfirmWindow, WRITES_ENABLED } from '@/components/MaintenanceForm';
import { completeTaobaoOrder, deleteTaobaoDraft } from './api';
import { TaobaoReturnDialog } from './TaobaoReturnDialog';
import { useApi, useBack } from '../admin/kit/hooks';
import { lineKind, settlementType, taobaoStatus } from './status';
import type { TaobaoOrder, TaobaoOutbound, TaobaoReturn } from './types';
import s from './TaobaoDetail.module.css';

type Tab = 'lines' | 'outbound' | 'returns';

const unit = (t: string) => (t === 'BUNDLE' ? '套' : '个');

/** 出库记录按原订单行分组：套装一行「× N 套」，下面逐个组件列实际出库数（组件挂在套装那一行上） */
function OutboundTable({ order, rows }: { order: TaobaoOrder; rows: TaobaoOutbound[] }) {
  const byLine = new Map<string, TaobaoOutbound[]>();
  rows.forEach((r) => byLine.set(r.orderLineId, [...(byLine.get(r.orderLineId) || []), r]));
  const lines = order.lines.filter((l) => byLine.has(l.id));
  return (
    <table className={s.tbl}>
      <thead><tr><th>实际出库产品</th><th className={s.r}>出库数量</th><th className={s.hideNarrow}>仓库</th><th className={s.hideNarrow}>操作人</th><th className={s.r}>时间</th></tr></thead>
      <tbody>
        {lines.map((l) => {
          const recs = byLine.get(l.id)!;
          if (l.lineType !== 'BUNDLE') {
            return recs.map((r) => (
              <tr key={r.id}>
                <td>{r.product.name} <span className="em tnum">{r.product.code}</span></td>
                <td className={s.r}>{qty(r.quantity)}</td>
                <td className={s.hideNarrow}>{r.warehouse.name}</td>
                <td className={s.hideNarrow}>{r.createdBy.name}</td>
                <td className={`${s.r} em`}>{dateTime(r.createdAt)}</td>
              </tr>
            ));
          }
          return [
            <tr key={l.id} className={s.kit}>
              <td>{l.bundle?.name || '套装'} <Tag tone="violet" sm>套装</Tag></td>
              <td className={s.r}>{qty(l.quantity)} 套</td>
              <td className={s.hideNarrow} />
              <td className={s.hideNarrow} />
              <td />
            </tr>,
            ...recs.map((r) => (
              <tr key={r.id} className={s.child}>
                <td>{r.product.name} <span className="em tnum">{r.product.code}</span></td>
                <td className={s.r}>{qty(r.quantity)} 个</td>
                <td className={s.hideNarrow}>{r.warehouse.name}</td>
                <td className={s.hideNarrow}>{r.createdBy.name}</td>
                <td className={`${s.r} em`}>{dateTime(r.createdAt)}</td>
              </tr>
            )),
          ];
        })}
      </tbody>
    </table>
  );
}

/**
 * 淘宝店订单详情。老接口的详情只带明细和计数，出库、退货记录按单号去两个列表接口取，再按订单 id 精确过滤。
 * 通过 V2 入口复用原有出库与退货事务。
 */
export function TaobaoDetail({ id, embedded }: { id: string; embedded?: boolean }) {
  const nav = useNavigate();
  const can = useSession((state) => state.can);
  const user = useSession((state) => state.user);
  const [action, setAction] = useState<'outbound' | 'return' | 'delete' | null>(null);
  const ff = useFormFactor();
  const back = useBack('/taobao');
  const { data: o, loading, error, reload } = useApi<TaobaoOrder>(`/api/taobao/orders/${id}`);
  const outbound = useApi<Page<TaobaoOutbound>>(o ? '/api/taobao/outbound-records' : null, o ? { keyword: o.orderNo, pageSize: 100 } : undefined);
  const returns = useApi<Page<TaobaoReturn>>(o ? '/api/taobao/returns' : null, o ? { keyword: o.orderNo, pageSize: 100 } : undefined);
  const [tab, setTab] = useState<Tab>('lines');

  if (error) return <ErrorState title="淘宝店订单加载失败" message={error.message} detail={`GET /api/taobao/orders/${id}`} onRetry={reload} />;
  if (loading || !o) return <div className={s.loading}><Skeleton /><Skeleton lines={6} /></div>;

  const st = taobaoStatus[o.status] || { label: o.status, tone: 'none' as const };
  const settle = settlementType[o.settlementType] || { label: o.settlementType, dot: 'green' as const };
  const outRows = (outbound.data?.items || []).filter((r) => r.orderId === o.id);
  const retRows = (returns.data?.items || []).filter((r) => r.orderId === o.id);
  const outCount = o._count?.outboundRecords ?? outRows.length;
  const retCount = o._count?.returns ?? retRows.length;
  const phone = ff === 'phone';
  const wide = !embedded && !phone;

  const info = (
    <>
      <section className={s.panel}>
        <h3>结算</h3>
        <dl className={s.kv}>
          <div><dt>结算方式</dt><dd><Dot tone={settle.dot}>{settle.label}</Dot></dd></div>
          {o.paymentMethod && <div><dt>付款方式</dt><dd>{o.paymentMethod}</dd></div>}
          {o.settlementType === 'MONTHLY' && <div><dt>到期日</dt><dd className="tnum">{date(o.dueDate)}</dd></div>}
          <div><dt>已收</dt><dd className="tnum">{money(o.paidAmount)}</dd></div>
          <div><dt>未收</dt><dd className={`tnum ${num(o.unpaidAmount) > 0 ? s.warn : ''}`}>{money(o.unpaidAmount)}</dd></div>
        </dl>
      </section>
      <section className={s.panel}>
        <h3>客户与仓库</h3>
        <dl className={s.kv}>
          <div><dt>客户</dt><dd>{o.customer.name}</dd></div>
          {(o.customer.contact || o.customer.phone) && <div><dt>联系人</dt><dd>{o.customer.contact || '—'} {o.customer.phone && <span className="tnum em">{o.customer.phone}</span>}</dd></div>}
          <div><dt>出库仓库</dt><dd>{o.warehouse.name}<span className="em">（{o.warehouse.code}）</span></dd></div>
          <div><dt>开单人</dt><dd>{o.createdBy.name}</dd></div>
          <div><dt>下单日期</dt><dd className="tnum">{date(o.orderDate)}</dd></div>
          {o.completedAt && <div><dt>出库时间</dt><dd className="tnum">{dateTime(o.completedAt)}</dd></div>}
          {o.note && <div><dt>备注</dt><dd>{o.note}</dd></div>}
        </dl>
      </section>
    </>
  );

  const main = (
    <>
      {o.status === 'DRAFT' && <Alert tone="info" title="草稿还没出库">确认出库后才扣库存、计应收。请核对明细后点击「确认出库」。</Alert>}
      <div className={s.kpis}>
        <div><span>订单总价</span><b className="tnum">{money(o.totalAmount)}</b></div>
        <div><span>优惠</span><b className="tnum">{num(o.discountAmount) > 0 ? `-${money(o.discountAmount)}` : money(0)}</b></div>
        <div><span>退货</span><b className={`tnum ${num(o.returnAmount) > 0 ? s.warn : ''}`}>{money(o.returnAmount)}</b></div>
        <div><span>应收净额</span><b className={`tnum ${s.hl}`}>{money(o.netAmount)}</b></div>
      </div>

      <div className={s.tabs} role="tablist">
        {([['lines', '明细', o.lines.length], ['outbound', '出库', outCount], ['returns', '退货', retCount]] as [Tab, string, number][]).map(([k, label, n]) => (
          <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? s.on : ''} onClick={() => setTab(k)}>
            {label}{n > 0 && <i className="tnum">{n}</i>}
          </button>
        ))}
      </div>
      <div className={s.tabBody}>
        {tab === 'lines' && (
          <table className={s.tbl}>
            <thead><tr><th>销售项目</th><th className={s.hideNarrow}>编号</th><th className={s.r}>数量</th><th className={s.r}>已退</th><th className={`${s.r} ${s.hideNarrow}`}>单价</th><th className={s.r}>金额</th></tr></thead>
            <tbody>
              {o.lines.map((l) => (
                <tr key={l.id}>
                  <td><span className={s.kind}>{lineKind(l.lineType)}</span>{l.bundle?.name || l.product?.name || '—'}{l.note && <small className={s.lnote}>{l.note}</small>}</td>
                  <td className={`em tnum ${s.hideNarrow}`}>{l.bundle?.code || l.product?.code || '—'}</td>
                  <td className={s.r}>{qty(l.quantity)} {unit(l.lineType)}</td>
                  <td className={`${s.r} ${num(l.returnedQuantity) > 0 ? s.warn : 'em'}`}>{num(l.returnedQuantity) > 0 ? qty(l.returnedQuantity) : '—'}</td>
                  <td className={`${s.r} ${s.hideNarrow}`}>{money(l.unitPrice)}</td>
                  <td className={s.r}>{money(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === 'outbound' && (outbound.loading ? <div className={s.loading}><Skeleton lines={3} /></div>
          : outbound.error ? <p className={s.none}>出库记录加载失败：{outbound.error.message}</p>
          : outRows.length ? <OutboundTable order={o} rows={outRows} />
          : <p className={s.none}>{o.status === 'DRAFT' ? '草稿还没有出库。' : '没有出库记录。'}</p>)}
        {tab === 'returns' && (returns.loading ? <div className={s.loading}><Skeleton lines={3} /></div>
          : returns.error ? <p className={s.none}>退货记录加载失败：{returns.error.message}</p>
          : retRows.length ? (
            <ul className={s.rets}>
              {retRows.map((r) => (
                <li key={r.id}>
                  <div className={s.rh}><b className="tnum">{r.returnNo}</b><span className="tnum em">{dateTime(r.returnedAt)} · {r.createdBy.name}</span><b className={`tnum ${s.warn}`}>{money(r.amount)}</b></div>
                  <p>{r.lines.map((x) => `${lineKind(x.orderLine.lineType)} ${x.orderLine.bundle?.name || x.orderLine.product?.name || ''} × ${qty(x.quantity)}`).join('；')}</p>
                  {r.note && <p className="em">{r.note}</p>}
                </li>
              ))}
              <li className={s.retNote}>退货确认后已按实际组成数量恢复到原仓库（{o.warehouse.name}），并写入库存流水。</li>
            </ul>
          ) : <p className={s.none}>没有退货记录。</p>)}
      </div>
    </>
  );

  return (
    <div className={`${s.page} ${embedded ? s.embedded : ''} ${phone ? s.phone : ''}`}>
      <header className={s.head}>
        {!embedded && <button className={s.back} onClick={back} aria-label="返回"><Icon name="back" size={20} /></button>}
        <h1 className="tnum">{o.orderNo}</h1>
        <Tag tone={st.tone}>{st.label}</Tag>
        <span className={s.sp} />
        {embedded && <Link className={s.open} to={`/taobao/${o.id}`}>全屏查看</Link>}
        {can('taobao.manage') && o.status === 'DRAFT' && <><Link className={s.legacy} to={`/taobao/${o.id}/edit`}>编辑</Link><button type="button" className={s.legacy} disabled={!WRITES_ENABLED} onClick={() => setAction('outbound')}>确认出库</button>{user?.role === 'ADMIN' && <button type="button" className={s.legacy} disabled={!WRITES_ENABLED} onClick={() => setAction('delete')}>删除草稿</button>}</>}
        {WRITES_ENABLED && can('taobao.manage') && user?.role === 'ADMIN' && o.status === 'COMPLETED' && Number(o.returnAmount) === 0 && o.lines.every((line) => Number(line.returnedQuantity) === 0) && <Link className={s.legacy} to={`/taobao/${o.id}/edit`}>修正金额</Link>}
        {can('taobao.manage') && ['COMPLETED', 'PARTIALLY_RETURNED'].includes(o.status) && <button type="button" className={s.legacy} disabled={!WRITES_ENABLED} onClick={() => setAction('return')}>办理退货</button>}
      </header>
      <div className={s.scroll}>
        <p className={s.sub}>{o.customer.name} · {o.warehouse.name} · {o.createdBy.name}开单 · {date(o.orderDate)} · <Dot tone={settle.dot}>{settle.label}{o.paymentMethod ? ` · ${o.paymentMethod}` : ''}</Dot></p>
        {wide ? <div className={s.grid}><div className={s.col}>{main}</div><aside className={s.col}>{info}</aside></div> : <div className={s.col}>{main}{info}</div>}
      </div>
      {action === 'outbound' && <ConfirmWindow title={`确认出库 · ${o.orderNo}`} confirm="确认出库" disabled={!WRITES_ENABLED} onClose={() => setAction(null)} onConfirm={async () => { await completeTaobaoOrder(o.id); setAction(null); reload(); outbound.reload(); }}><p>将从「{o.warehouse.name}」扣减库存，套装按组件出库。现结订单按所选收款方式记账，月结订单计入应收。</p></ConfirmWindow>}
      {action === 'delete' && <ConfirmWindow title={`删除草稿 · ${o.orderNo}`} confirm="确认删除" disabled={!WRITES_ENABLED} onClose={() => setAction(null)} onConfirm={async () => { await deleteTaobaoDraft(o.id); setAction(null); nav('/taobao'); }}><p>只有未出库的草稿可以删除。</p></ConfirmWindow>}
      {action === 'return' && <TaobaoReturnDialog order={o} onClose={() => setAction(null)} onDone={() => { setAction(null); reload(); returns.reload(); }} />}
    </div>
  );
}

export function TaobaoDetailPage() {
  const { id } = useParams();
  return <TaobaoDetail id={id!} />;
}
